import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { PharmaNetContext, PharmaNetCredentials } from "@/Common/Components/Billing/PharmaNetCredentials";
import { MedicalItemsTab, PatientMedicationAction } from "@/Common/Models/Patient/PatientEnums";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { PatientMedicalItemsHelper } from "@/Patient/Helpers/PatientMedicalItemsHelper";
import { MedicalItemTypeEnum } from "@/Patient/Models/MedicalSummary/Enum/MedicalItemTypeEnum";
import { MedicationModel } from "@/Patient/Models/Medication/MedicationModel";
import { MedicalItemViewMode } from "@/Patient/Models/UI/MedicalNote/Enums/MedicalItem";
import { PatientMedicationStateManager } from "../Medication/PatientMedicationStateManager";
import { PatientMedicationView, PatientMedicationViewState } from "../Medication/PatientMedicationView";
import { BaseMedicalItemsView, IMedicalItemsViewProps } from "./BaseMedicalItemsView";
import { BaseMedicalItemsViewState } from "./BaseMedicalItemsViewState";

class ActiveMedicationViewState extends BaseMedicalItemsViewState {
    public modelToRender: MedicationModel = null;
    public patientMedicationState: PatientMedicationViewState = null;

    constructor(s?: Partial<ActiveMedicationViewState>) {
        super(s);
        Object.assign(this, s);
    }
}

class MedicalItemsActiveMedicationView extends BaseReactComponent<IMedicalItemsViewProps, ActiveMedicationViewState> {
    private patientMedicationViewRef: React.RefObject<PatientMedicationView>;

    public constructor(props: IMedicalItemsViewProps) {
        super(props);
        this.patientMedicationViewRef = React.createRef();
        this.state = this.getDefaultState();
    }

    public componentDidMount(): void {
        $(window).trigger("resize");
    }

    public shouldComponentUpdate(nextProps: IMedicalItemsViewProps, nextState: ActiveMedicationViewState): boolean {
        return (
            this.state.modelToRender != null
            && this.state.modelToRender.Model != null
            && nextState.modelToRender != null
            && nextState.modelToRender.Model != null
            && nextState.modelToRender?.Model != this.state.modelToRender.Model
        )
            || (
                this.state.patientMedicationState !== null
                && this.state.patientMedicationState !== nextState.patientMedicationState
            );
    }

    public componentDidUpdate(prevProps: IMedicalItemsViewProps): void {
        if (this.props.Model !== prevProps.Model) {
            this.setState(this.getDefaultState());
        }

        $(window).trigger("resize");
    }

    public componentWillUnmount(): void {
        patientStore.setMedicalItemCachedState(this.props.PatientContactId, this.getItemCacheId(this.props), this.state);
    }

    private getDefaultState(): ActiveMedicationViewState {
        let newState: ActiveMedicationViewState = null;

        let cacheId = this.getItemCacheId(this.props);
        if (cacheId != null)
            newState = patientStore.getMedicalItemCachedState(this.props.PatientContactId, cacheId) as ActiveMedicationViewState;

        if (newState == null)
            newState = new ActiveMedicationViewState({
                modelToRender: MedicationModel.FromDefaultModel(true, true)
            });
        else
            newState.modelToRender = newState.patientMedicationState.baseMedicationUpdate;

        return newState;
    }

    public render(): JSX.Element {
        return (
            <BaseMedicalItemsView
                PatientContactId={this.props.PatientContactId}
                Model={this.props.Model}
                DetailsTitle={cacheStore.GetString("CppLabel") + " : " + cacheStore.GetString("CurrentMedicationLabel").toTitleCase()}
                ListItemRenderFunction={() => <div />}
                MedicalItemRenderFunction={() => <div />}
                MedicalItemEditRenderFunction={() => (
                    <div className="medical-reaction-detail-box">
                        <div style={{ "marginLeft": "50px" }}>
                            <PatientMedicationView
                                ref={this.patientMedicationViewRef}
                                onStateUpdate={childState => this.mutateState(s => s.patientMedicationState = childState)}
                                baseMedication={this.state.modelToRender}
                                useDefaultLongTermValueAndDuration={cacheStore.ClinicSettings.EnableLongTermMedication}
                            />
                        </div>
                    </div>
                )}
                SaveFunction={() => this.save()}
                SavePlusFunction={() => this.savePlus()}
                emrType={MedicalItemTypeEnum.MedicationsAndPrescriptions}
                CheckIfSaveEnabledFunction={() => this.checkIfSaveIsEnabled()}
                IsForEditActiveMedication
                additionalLeftControlButton={this.getPharmaNetButton()}
                childActionPopup={this.renderPharmaNetLoginComponent()}
                showFavorites={this.props.showFavorites}
            />
        );
    }

    private getCurrentStateManager(): PatientMedicationStateManager {
        return new PatientMedicationStateManager(this.state.patientMedicationState);
    }

    private async savePlus(): Promise<void> {
        await this.save(true);
        this.patientMedicationViewRef.current.setSelectedAction(PatientMedicationAction.CompleteAndReplace);
        this.patientMedicationViewRef.current.resetDateTexts();
    }

    private async save(isSavePlus: boolean = false): Promise<void> {
        if (IsNullOrWhiteSpace(this.state.patientMedicationState.baseMedicationUpdate.Model.Name))
            return;

        if (patientStore.IsMedicalItemFromOutsideNote) {
            await PatientMedicalItemsHelper.setOutsideNoteEncounter();
        }

        sessionStore.dispatch(
            LocalPatientOperations.MedicalItems.Save,
            {
                view: MedicalItemsTab.Medication,
                editedItems: [],
                isSavePlus,
                baseMedicationItem: this.state.patientMedicationState?.baseMedicationUpdate,
                newMedicationItem: this.state.patientMedicationState?.newMedication
            }
        );
    }

    private checkIfSaveIsEnabled(): boolean {
        return this.state.patientMedicationState
            && this.getCurrentStateManager().hasValidMedicationName();
    }

    private getItemCacheId(props: IMedicalItemsViewProps): string {
        const viewMode = props.Model?.get("ViewMode");
        const items = props.Model?.get("MedicalItemsList");

        if (viewMode == MedicalItemViewMode.Edit && items?.count())
            return items.find(obj => obj.IsCommitted === false)?.Id;

        return "";
    }

    private getPharmaNetButton(): JSX.Element {
        if (!cacheStore.hasPharmaNetAccess())
            return null;

        return (
            <button
                className="btn btn-primary"
                onClick={() => this.togglePharmaNetComponent()}
            >
                {cacheStore.GetString("PharmaNet")}
            </button>
        );
    }

    protected renderPharmaNetLoginComponent(): JSX.Element {
        if (!this.props.Model.get("ShowPharmaNetComponent"))
            return null;

        return (
            <PharmaNetCredentials
                onClose={() => this.togglePharmaNetComponent()}
                patient={patientStore.PatientModels[this.props.PatientContactId]}
                context={PharmaNetContext.Patient}
            />
        );
    }

    protected togglePharmaNetComponent(): void {
        sessionStore.dispatch(LocalPatientOperations.MedicalItems.TogglePharmaNetLoginComponent);
    }
}

export { MedicalItemsActiveMedicationView };
